'use client';
import React, { useEffect, useRef, useState } from 'react';
import {
  BlockedState,
  CheckoutBillingAddress,
} from '~/gql/__generated__/types';
import getStatesAndProvinces from '~/services/actions/CSU/get-states-provinces';
import {
  getBlockedStateMessage,
  getFilteredStatesFromBlockStates,
} from '~/utils/get-block-message';
import {
  customerInfoConstants,
  ICustomerInfoConstantsInputsProperties,
} from './constants';

import {
  lookupAddressFieldError,
  IFormInitialState,
  prepareBillingInitialFormState,
  // prepareBillingFormStateWithShipping,
} from './errorLogic';
import { validatZipPostalInfo } from '~/utils/validate-zip-postal-info';
import { onBlurHandler } from './components/on-blur-handler';
import Styles from './billing-address.module.scss';
import AddressForm from './components/address-form';
import {
  IStateProvinces,
  IGetStateProvincesResponse,
} from '~/interfaces/states-provinces';
import {
  usePaymentReview,
  // useSelectedPaymentOption,
  useSetBillingAddress,
  useSetIsLoading,
} from '~/store/wmap-store';

/*import {
  INPUT_ID_SAME_AS_DELIVERY,
  INPUT_ID_DIFFERENT_BILLING_ADDRESS,
  LABEL_ID_SAME_AS_DELIVERY,
  LABEL_ID_DIFFERENT_BILLING_ADDRESS,
} from '~/constant/constants';

import { IAddressFields } from '~/interfaces/address-fields';
import { WMAP_PREFIX, TYPES } from '~/modules/payment/constants';*/

export interface IPrevRefValues {
  [key: string]: string | boolean;
}

export interface IBillingAddressProps {
  blockedStateList: BlockedState[];
  billingAddressConfigs: CheckoutBillingAddress;
  //shippingAddress: IAddressFields;
}

const getInitialRefValues = () => {
  return {
    firstName: '',
    lastName: '',
    emailAddress: '',
    phoneNumber: '',
    address: '',
    apartment: '',
    zipcode: '',
    city: '',
    state: '',
  };
};

const BillingAddress: React.FC<IBillingAddressProps> = ({
  blockedStateList,
  billingAddressConfigs,
  //shippingAddress,
}) => {
  const setBillingAddress = useSetBillingAddress();
  const setIsLoading = useSetIsLoading();
  /*const [billingOption, setBillingOption] = useState<string>(
    INPUT_ID_SAME_AS_DELIVERY,
  );
  const billingAdressData =
    prepareBillingFormStateWithShipping(shippingAddress);
  if (billingOption !== INPUT_ID_DIFFERENT_BILLING_ADDRESS) {
    setBillingAddress(billingAdressData);
  }
  */
  const [stateProvinces, setStateProvinces] = useState<IStateProvinces[]>([]);
  useEffect(() => {
    const fetchData = async () => {
      const result = (await getStatesAndProvinces('USA').catch(
        () => [],
      )) as IGetStateProvincesResponse;
      setStateProvinces(result.value);
    };
    fetchData();
  }, []);

  const filteredstateProvincesList = getFilteredStatesFromBlockStates(
    stateProvinces,
    blockedStateList,
  );
  const addressFocus = useRef<boolean>(false);

  const [billingZipLoading, setBillingZipLoading] = useState<boolean>(false);
  const [isZipUpdated, setIsZipUpdated] = useState<boolean>(false);
  const [isStateUpdated, setIsStateUpdated] = useState<boolean>(false);

  const {
    firstName,
    lastName,
    emailAddress,
    phoneNumber,
    zipcode,
    address,
    city,
    state,
  } = customerInfoConstants.inputs;

  const [zipcodetemp, setZipcodetemp] = useState<string>('');
  const [statetemp, setStatetemp] = useState<string>('');
  const [formState, setFormState] = useState<IFormInitialState>(() =>
    prepareBillingInitialFormState(),
  );
  const prevRefValues = useRef<IPrevRefValues>(getInitialRefValues());
  const [isBlockedZip, setIsBlockedZip] = useState<boolean>(false);

  const zipCode = formState.zipcode.value.toString();
  const stateName = formState.state.value.toString();
  const latestZipCodeRef = useRef(zipCode);

  useEffect(() => {
    let isMounted = true;

    const validateZipCode = async () => {
      if (isBlockedZip) {
        setIsBlockedZip(false);
      }

      if (zipCode.length < 5) return;

      const isZipChanged = zipcodetemp !== zipCode && isZipUpdated;
      const isStateChanged = statetemp !== stateName && isStateUpdated;

      if (isZipChanged || isStateChanged) {
        setIsLoading(true);
        if (isZipChanged) setBillingZipLoading(true);
      }

      setZipcodetemp(zipCode);
      setStatetemp(stateName);

      try {
        latestZipCodeRef.current = zipCode;
        const result = await validatZipPostalInfo(
          zipCode,
          stateName,
          blockedStateList,
        );

        if (!isMounted || latestZipCodeRef.current !== zipCode) return;

        const res = JSON.parse(result);

        if (res.error === 'invalid') {
          handleStateChange(zipcode.name, zipCode, zipcode.errorMsg);
        } else if (res.error === 'blocked' && res.message?.length > 0) {
          handleStateChange(zipcode.name, zipCode, res.message);
        }

        if (
          res.stateError === 'invalid' &&
          (formState.state.error === '' ||
            formState.state.error === state.errorMsg ||
            formState.state.error === state.requiredMsg)
        ) {
          handleStateChange(state.name, stateName, state.errorMsg);
        } else if (
          stateName?.length > 0 &&
          formState.state.error === state.errorMsg
        ) {
          handleStateChange(state.name, stateName, '');
        }
      } catch (error) {
        console.error('ZIP code action error:', error);
      } finally {
        if (isMounted) {
          setBillingZipLoading(false);
          setIsZipUpdated(false);
          setIsStateUpdated(false);
          setIsLoading(false);
        }
      }
    };

    validateZipCode();

    return () => {
      isMounted = false;
    };
  }, [zipCode, stateName, isZipUpdated, isStateUpdated, blockedStateList]);

  useEffect(() => {
    latestZipCodeRef.current = zipCode;
    handleZipCodeErrors(zipCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zipCode]);

  useEffect(() => {
    const inputElement = document.getElementsByName(firstName.name)[0];
    if (formState.firstName.value && inputElement) {
      inputElement.focus();
    }
  }, [formState.firstName.value, firstName.name]);

  const handleSpecificChange = (
    inputConstants: ICustomerInfoConstantsInputsProperties,
    inputName: string,
    inputValue: string,
  ) => {
    const errorMsg = lookupAddressFieldError(inputConstants, inputValue);
    if (errorMsg !== undefined) {
      handleStateChange(inputName, inputValue, errorMsg);
    } else if (inputName === 'zipcode') {
      handleZipCodeErrors(inputValue);
    } else if (inputName === 'state') {
      handleStateErrors(inputValue);
    } else {
      handleStateChange(inputName, inputValue);
    }
  };

  const handleStateChange = (
    name: string,
    value: string | boolean,
    errorMsg?: string,
  ) => {
    if (
      name === 'zipcode' &&
      typeof value === 'string' &&
      !/^\d*$/.test(value)
    ) {
      return;
    }
    setFormState((prevState: IFormInitialState): IFormInitialState => {
      const data: IFormInitialState = {
        ...prevState,
        [name]: {
          value: value.toString(),
          error: errorMsg ?? '',
          validation: customerInfoConstants.inputs[name],
        },
      };
      setBillingAddress(data);
      return data;
    });
  };

  const handleZipCodeErrors = (value: string | boolean): void => {
    if (isInvalidZipCode(value.toString())) {
      handleStateChange(zipcode.name, value, zipcode.errorMsg);
      return;
    }

    if (isShortZipCode(value)) return;

    handleStateChange(zipcode.name, value.toString(), '');
    setBillingZipLoading(false);
  };

  const isInvalidZipCode = (zipCode: string) => {
    return zipCode === '00000';
  };

  const isShortZipCode = (value: string | boolean): boolean => {
    return value.toString().length < 5;
  };

  const handleStateErrors = (value: string | boolean) => {
    const blockedStateMsg = getBlockedStateMessage(
      String(value),
      blockedStateList,
    );
    handleStateChange(state.name, value, blockedStateMsg);
  };
  const changeHandler = (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const name: string = event.target.name;
    const value: string = event.target.value;
    if (formState[name]?.value === value && value !== '') return;
    switch (name) {
      case 'firstName':
        handleSpecificChange(firstName, name, value);
        break;
      case 'lastName':
        handleSpecificChange(lastName, name, value);
        break;
      case 'emailAddress':
        handleSpecificChange(emailAddress, name, value);
        break;
      case 'phoneNumber':
        handleSpecificChange(phoneNumber, name, value);
        break;
      case 'zipcode':
        setIsZipUpdated(true);
        handleSpecificChange(zipcode, name, value);
        break;
      case 'city':
        handleSpecificChange(city, name, value);
        break;
      case 'address':
        handleSpecificChange(address, name, value);
        break;
      case 'state':
        setIsStateUpdated(true);
        handleSpecificChange(state, name, value);
        break;
      default:
        handleStateChange(name, value);
        break;
    }
  };
  /* const billingOptionClick = (option: string) => {
    setBillingOption(option);
    if (option === INPUT_ID_SAME_AS_DELIVERY) {
      setBillingAddress(billingAdressData);
    } else {
      const initialRefValues = getInitialRefValues();
      setBillingAddress(formState);
      prevRefValues.current = initialRefValues;
    }
  };
*/
  const handleFocus = () => {
    addressFocus.current = true;
  };
  //const selectedPayment = useSelectedPaymentOption();
  //do not render this component for payment review page
  const paymentReview = usePaymentReview();
  if (paymentReview.review) {
    return <></>;
  }

  return (
    // selectedPayment === TYPES.CREDIT_CARD && (
    <section className={Styles.billingForm}>
      <h4 className="mb-0" tabIndex={0}>
        Billing Address
      </h4>
      {/*
        <p className={Styles.subHeading} tabIndex={0}>
          Select the address that matches your card or payment method.
        </p>
        */}

      <div className={Styles.addressOptionsWrapper}>
        {/*
          <label
            className={`${Styles.radioLabel} ${
              billingOption === INPUT_ID_SAME_AS_DELIVERY ? Styles.checked : ''
            }`}
            id={`${LABEL_ID_SAME_AS_DELIVERY}`}
            htmlFor={`${WMAP_PREFIX}-${INPUT_ID_SAME_AS_DELIVERY}`}
            onClick={() => billingOptionClick(INPUT_ID_SAME_AS_DELIVERY)}
          >
            <input
              type="radio"
              id={`${WMAP_PREFIX}-${INPUT_ID_SAME_AS_DELIVERY}`}
              name="sameAsDelivery"
              checked={billingOption === INPUT_ID_SAME_AS_DELIVERY}
              onChange={() => billingOptionClick(INPUT_ID_SAME_AS_DELIVERY)}
            />
            <div className={Styles.radioIcon}>
              <span className={Styles.label}>Same as delivery address</span>
            </div>
          </label>

         
          <label
            className={`${Styles.radioLabel} ${
              billingOption === INPUT_ID_DIFFERENT_BILLING_ADDRESS
                ? Styles.checked
                : ''
            }`}
            id={`${LABEL_ID_DIFFERENT_BILLING_ADDRESS}`}
            htmlFor={`${WMAP_PREFIX}-${INPUT_ID_DIFFERENT_BILLING_ADDRESS}`}
            onClick={() =>
              billingOptionClick(INPUT_ID_DIFFERENT_BILLING_ADDRESS)
            }
          >
            <input
              type="radio"
              id={`${WMAP_PREFIX}-${INPUT_ID_DIFFERENT_BILLING_ADDRESS}`}
              name="differentBillingAddress"
              checked={billingOption === INPUT_ID_DIFFERENT_BILLING_ADDRESS}
              onChange={() =>
                billingOptionClick(INPUT_ID_DIFFERENT_BILLING_ADDRESS)
              }
            />
            <div className={Styles.radioIcon}>
              <span className={Styles.label}>
                Use a different billing address
              </span>
            </div>
          </label>
          */}
        <div className={Styles.addressFormWrapper}>
          <AddressForm
            formState={formState}
            changeHandler={changeHandler}
            onBlurHandler={onBlurHandler}
            handleFocus={handleFocus}
            isZipcodeUpdating={billingZipLoading}
            addressFocus={addressFocus}
            stateProvinces={filteredstateProvincesList}
            zipCode={zipCode}
            prevRefValues={prevRefValues}
            updateFormState={handleStateChange}
            billingAddressConfigs={billingAddressConfigs}
          />
        </div>
      </div>
    </section>
  );
  // );
};
export default BillingAddress;
